import { FieldErrors, UseFormRegister } from "react-hook-form";
import get from "lodash.get";
import { useTranslation } from "react-i18next";

import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import PhoneInput from "@/components/phone-input";
import { isValidPhone, normalizePhone } from "@/utils/phone";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";

interface FormInputProps {
  name: string;
  label?: string;
  placeholder: string;
  /** `tel` keeps the value as digits and shows it dashed ({@link PhoneInput}). */
  type?: "text" | "textarea" | "email" | "tel";
  variant?: "flat" | "bordered" | "faded" | "underlined";
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger";
  size?: "sm" | "md" | "lg";
  radius?: "none" | "sm" | "md" | "lg" | "full";
  isRequired?: boolean;
  disabled?: boolean;
  className?: string;
  description?: string;
  /**
   * A phone number: shown as a `tel` field ({@link PhoneInput}), and the form
   * refuses to submit anything but exactly 10 digits.
   */
  phone?: boolean;
  formFieldParams?: {
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    loading: boolean;
    watch: (field: string) => any;
  };
  form?: any;
}

export const FormInput = ({
  name,
  className,
  label,
  variant = "bordered",
  color = "default",
  type = "text",
  radius = "sm",
  size = "sm",
  placeholder,
  description = "",
  isRequired = false,
  disabled = false,
  phone = false,
  form = null,
  formFieldParams,
  ...rest
}: FormInputProps) => {
  const { t } = useTranslation();
  const errors = [];
  const shortName = name?.split(".").pop();

  let value = "";

  if (formFieldParams) {
    value = formFieldParams.watch(name);
  }

  if (
    shortName &&
    formFieldParams &&
    typeof formFieldParams?.errors[shortName]?.message !== "undefined"
  ) {
    errors.push(formFieldParams?.errors[shortName]?.message);
  }
  if (formFieldParams) {
    const subError = get(formFieldParams?.errors, name);

    if (subError) {
      errors.push(subError.message);
    }
  }

  if (form !== null) {
    return (
      <FormField
        control={form.control}
        name={name}
        rules={
          phone
            ? {
                validate: (current: string | null | undefined) =>
                  !current ||
                  isValidPhone(normalizePhone(current)) ||
                  t("validation.phone", "Enter a 10-digit phone number, e.g. 5148257340"),
              }
            : undefined
        }
        render={({ field }) => (
          <FormItem className={className}>
            {label && (
              <FormLabel className="gap-1">
                {label}{" "}
                {isRequired && <span className="text-destructive">*</span>}
              </FormLabel>
            )}
            <FormControl>
              {phone || type === "tel" ? (
                <PhoneInput
                  disabled={disabled}
                  name={field.name}
                  placeholder={placeholder}
                  value={field.value}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                />
              ) : (
                <Input
                  placeholder={placeholder}
                  {...field}
                  disabled={disabled}
                />
              )}
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  return (
    <Field className={className}>
      {label && <FieldLabel htmlFor={name}>{label}</FieldLabel>}
      <Input
        id={name}
        value={value ?? ""}
        required={!!isRequired}
        placeholder={placeholder}
        disabled={disabled}
        {...rest}
      />
      {description && <FieldDescription>{description}</FieldDescription>}
    </Field>
  );

  // return (
  //   <div className={className}>
  //     <div
  //       className={"text-xs leading-4 font-medium mb-2 text-zinc-400 uppercase"}
  //     >
  //       {label ?? ""}
  //     </div>
  //     <Input
  //       classNames={{
  //         inputWrapper: variant === "underlined" ? "border-b" : "border",
  //       }}
  //       value={value ?? ""}
  //       {...formFieldParams?.register(name)}
  //       color={color}
  //       description={description}
  //       errorMessage={errors[0]?.toString().replace(/<[^>]*>/g, "")}
  //       isInvalid={!!errors.length}
  //       isRequired={isRequired}
  //       placeholder={placeholder}
  //       radius={radius}
  //       size={size}
  //       type={type}
  //       variant={variant}
  //     />
  //   </div>
  // );
};
